// src/components/user/express/Step3Vehicle.jsx
import React, { useEffect, useRef } from 'react';
import { Car, AlertCircle, Loader2, RotateCcw } from 'lucide-react';

export function Step3Vehicle({
  pricingConfig,
  vehicleType, setVehicleType,
  routeResult, calculatingRoute, routeError,
  calculateRoute,
  pickupLat, pickupLng,
  dropoffLat, dropoffLng,
}) {
  const mapRef      = useRef(null);
  const mapInstance = useRef(null);
  const routeRef    = useRef(null);
  const markersRef  = useRef([]);

  useEffect(() => {
    if (!routeResult?.polyline || !window.google) return;

    // Init map only once — reuse the same instance on recalculation
    if (!mapInstance.current) {
      mapInstance.current = new window.google.maps.Map(mapRef.current, {
        disableDefaultUI: true,
        zoomControl: true,
        styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
      });
    }

    // Clear previous polyline
    if (routeRef.current) routeRef.current.setMap(null);

    // Clear previous markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];

    const path = window.google.maps.geometry.encoding.decodePath(routeResult.polyline);

    routeRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: vehicleType === 'bike' ? '#67A43C' : '#B1336E',
      strokeOpacity: 0.9,
      strokeWeight: 5,
    });
    routeRef.current.setMap(mapInstance.current);

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(p => bounds.extend(p));
    mapInstance.current.fitBounds(bounds, 40);

    // Pickup marker
    const pickupMarker = new window.google.maps.Marker({
      position: { lat: pickupLat, lng: pickupLng },
      map: mapInstance.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#B1336E',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
    markersRef.current.push(pickupMarker);

    // Dropoff marker
    const dropoffMarker = new window.google.maps.Marker({
      position: { lat: dropoffLat, lng: dropoffLng },
      map: mapInstance.current,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 9,
        fillColor: '#67A43C',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
    markersRef.current.push(dropoffMarker);

  }, [routeResult]);

  const bike = pricingConfig?.bike;
  const car  = pricingConfig?.car;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-extrabold text-slate-800 font-montserrat mb-1">
          Vehículo y precio
        </h2>
        <p className="text-sm text-slate-500 font-montserrat">
          Elige el tipo de vehículo y calcula la ruta
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">

        {/* Moto */}
        <button
          onClick={() => setVehicleType('bike')}
          className={`relative flex flex-col items-center gap-2 py-5 px-3 rounded-2xl border-2 transition-all duration-200
            ${vehicleType === 'bike'
              ? 'border-brand-green bg-brand-green/5 shadow-md'
              : 'border-slate-200 bg-white hover:border-brand-green/40'}`}
        >
          {vehicleType === 'bike' && (
            <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-brand-green flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-white" />
            </div>
          )}
          <div className={`p-3 rounded-full transition-colors ${vehicleType === 'bike' ? 'bg-brand-green/15' : 'bg-slate-100'}`}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`w-8 h-8 ${vehicleType === 'bike' ? 'text-brand-green' : 'text-slate-400'}`}
              stroke="currentColor"
            >
              <circle cx="5.5" cy="17.5" r="3.5"/>
              <circle cx="18.5" cy="17.5" r="3.5"/>
              <path d="M15 6h-3l-2 4H4l2 4h1"/>
              <path d="M15 6l2 4h2"/>
              <path d="M9 10l2 4"/>
            </svg>
          </div>
          <span className={`text-sm font-extrabold font-montserrat ${vehicleType === 'bike' ? 'text-brand-green' : 'text-slate-500'}`}>
            Moto
          </span>
          {bike && (
            <div className="text-center">
              <p className={`text-xs font-montserrat font-semibold ${vehicleType === 'bike' ? 'text-slate-700' : 'text-slate-400'}`}>
                S/ {Number(bike.rate_per_km).toFixed(2)} / km
              </p>
              <p className={`text-xs font-montserrat ${vehicleType === 'bike' ? 'text-slate-500' : 'text-slate-300'}`}>
                Mín. S/ {Number(bike.minimum_fare).toFixed(2)}
              </p>
            </div>
          )}
        </button>

        {/* Auto */}
        <button
          onClick={() => setVehicleType('car')}
          className={`relative flex flex-col items-center gap-2 py-5 px-3 rounded-2xl border-2 transition-all duration-200
            ${vehicleType === 'car'
              ? 'border-brand-primary bg-brand-primary/5 shadow-md'
              : 'border-slate-200 bg-white hover:border-brand-primary/40'}`}
        >
          {vehicleType === 'car' && (
            <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-brand-primary flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-white" />
            </div>
          )}
          <div className={`p-3 rounded-full transition-colors ${vehicleType === 'car' ? 'bg-brand-primary/15' : 'bg-slate-100'}`}>
            <Car className={`w-8 h-8 ${vehicleType === 'car' ? 'text-brand-primary' : 'text-slate-400'}`} />
          </div>
          <span className={`text-sm font-extrabold font-montserrat ${vehicleType === 'car' ? 'text-brand-primary' : 'text-slate-500'}`}>
            Auto
          </span>
          {car && (
            <div className="text-center">
              <p className={`text-xs font-montserrat font-semibold ${vehicleType === 'car' ? 'text-slate-700' : 'text-slate-400'}`}>
                S/ {Number(car.rate_per_km).toFixed(2)} / km
              </p>
              <p className={`text-xs font-montserrat ${vehicleType === 'car' ? 'text-slate-500' : 'text-slate-300'}`}>
                Mín. S/ {Number(car.minimum_fare).toFixed(2)}
              </p>
            </div>
          )}
        </button>
      </div>

      {/* Calculate button */}
      <button
        onClick={calculateRoute}
        disabled={!vehicleType || calculatingRoute}
        className={`w-full py-3 rounded-xl font-montserrat font-bold text-sm transition-all flex items-center justify-center gap-2
          ${vehicleType && !calculatingRoute
            ? 'bg-brand-primary text-white hover:bg-brand-primary/90 shadow-md'
            : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
      >
        {calculatingRoute
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculando ruta…</>
          : <><RotateCcw className="w-4 h-4" /> Calcular Ruta</>
        }
      </button>

      {routeError && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600 font-montserrat">{routeError}</p>
        </div>
      )}

      {/* Map — always mounted, hidden until first result to avoid remount issue */}
      <div style={{ display: routeResult ? 'block' : 'none' }}>
        <div
          ref={mapRef}
          className="w-full h-52 rounded-xl overflow-hidden border-2 border-slate-200"
        />
      </div>

      {/* Price summary — only shown when result exists */}
      {routeResult && (
        <div className={`rounded-xl p-4 border-2 ${vehicleType === 'bike' ? 'border-brand-green/30 bg-brand-green/5' : 'border-brand-primary/30 bg-brand-primary/5'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-extrabold text-slate-800 font-montserrat">Resumen de precio</span>
            {routeResult.minimum_applied && (
              <span className="text-xs bg-brand-gold/20 text-brand-gold font-semibold font-montserrat px-2 py-0.5 rounded-full">
                Tarifa mínima
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs font-montserrat text-slate-600">
              <span>Distancia</span>
              <span className="font-semibold">{routeResult.distance_km} km</span>
            </div>
            <div className="flex justify-between text-xs font-montserrat text-slate-600">
              <span>Tarifa</span>
              <span className="font-semibold">S/ {Number(routeResult.rate_per_km).toFixed(2)} / km</span>
            </div>
            <div className="flex justify-between text-xs font-montserrat text-slate-600">
              <span>Tiempo estimado</span>
              <span className="font-semibold">{routeResult.duration_minutes} min</span>
            </div>
            <div className="border-t border-slate-200 pt-1.5 mt-1 flex justify-between font-montserrat">
              <span className="text-sm font-extrabold text-slate-800">Total</span>
              <span className={`text-lg font-extrabold ${vehicleType === 'bike' ? 'text-brand-green' : 'text-brand-primary'}`}>
                S/ {Number(routeResult.total_price).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}